import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { GlossaryModel, GlossaryRowModel } from '../../data-access-layer/interfaces/glossary.interface';
import { PythonService } from '../../data-access-layer/services/python.service';
import { AlertService } from '../../data-access-layer/services/alert.service';
import { saveAs } from 'file-saver-es';

@Component({
  selector: 'app-glossary',
  templateUrl: '../templates/glossary.component.html',
  styleUrl: '../templates/styles/glossary.component.css',
  standalone: false
})

export class GlossaryComponent implements OnInit {

  @ViewChild('importFileInput') importFileInput: ElementRef<HTMLInputElement> | null = null;

  public data: GlossaryRowModel[] = [];
  public companyDesc: string = '';
  public isEditCompany = false;
  private companyDescOriginal: string = '';
  public searchTerm = '';

  public termModalOpen = false;
  public editingTermRow: GlossaryRowModel | null = null;
  public modalTermName = '';
  public modalTermDescription = '';

  get filteredData(): GlossaryRowModel[] {
    const q = (this.searchTerm ?? '').trim().toLowerCase();
    if (!q) return this.data;
    return this.data.filter(
      (row) =>
        (row.term ?? '').toLowerCase().includes(q) ||
        (row.meaning ?? '').toLowerCase().includes(q)
    );
  }

  get isTermNameEmpty(): boolean {
    return !((this.modalTermName ?? '').trim() && (this.modalTermDescription ?? '').trim());
  }

  constructor(
    public pythonService: PythonService,
    private alertService: AlertService,
    private transloco: TranslocoService
  ) {
  }

  ngOnInit(): void {
    this.pythonService.getGlossaryTerms().subscribe((response) => {
      this.data = (response.rows ?? []).map((r: any) => ({
        rowId: r.row_id,
        term: r.term ?? '',
        meaning: r.meaning ?? '',
        isEdit: false,
        draftTerm: r.term ?? '',
        draftMeaning: r.meaning ?? '',
      }));
    });
    this.pythonService.getCompanyDescription().subscribe((response) => this.companyDesc = response.direction);
  }

  startEditCompanyDesc(): void {
    this.companyDescOriginal = this.companyDesc;
    this.isEditCompany = true;
  }

  cancelEditCompanyDesc(): void {
    this.companyDesc = this.companyDescOriginal;
    this.isEditCompany = false;
  }

  saveCompanyDesc(): void {
    this.pythonService.setCompanyDescription(this.companyDesc).subscribe((response) => {
      this.companyDesc = response.direction;
      this.isEditCompany = false;
    });
  }

  openTermModal(row?: GlossaryRowModel): void {
    this.editingTermRow = row ?? null;
    this.modalTermName = row ? (row.term ?? '') : '';
    this.modalTermDescription = row ? (row.meaning ?? '') : '';
    this.termModalOpen = true;
  }

  closeTermModal(): void {
    this.termModalOpen = false;
    this.editingTermRow = null;
  }

  submitTermModal(): void {
    const row = this.editingTermRow;
    const term = (this.modalTermName ?? '').trim();
    const meaning = (this.modalTermDescription ?? '').trim();
    if (row) {
      const payload = { row_id: row.rowId, term, meaning };
      this.pythonService.setGlossaryTerms(payload).subscribe({
        next: (updated) => {
          row.term = (updated as any)?.term ?? payload.term;
          row.meaning = (updated as any)?.meaning ?? payload.meaning;
          this.closeTermModal();
        },
        error: (err) => this.showSaveError(err),
      });
    } else {
      this.pythonService.addGlossaryTerm(term, meaning).subscribe({
        next: (created) => {
          const newRow: GlossaryRowModel = {
            rowId: (created as any)?.row_id ?? (created as any)?.rowId ?? 0,
            term: (created as any)?.term ?? term,
            meaning: (created as any)?.meaning ?? meaning,
            isEdit: false,
            draftTerm: '',
            draftMeaning: '',
          };
          this.data = [newRow, ...this.data];
          this.closeTermModal();
        },
        error: (err) => this.showSaveError(err),
      });
    }
  }

  deleteRow(row: GlossaryRowModel): void {
    if (row.isNew) {
      this.data = this.data.filter((r) => r !== row);
      return;
    }
    const termLabel = (row.term ?? '').trim() || this.transloco.translate('glossary.termUntitled');
    this.alertService.confirm({
      title: this.transloco.translate('glossary.deleteConfirmTitle'),
      message: this.transloco.translate('glossary.deleteConfirmMessage', { term: termLabel }),
      confirmButtonText: this.transloco.translate('dialog.delete'),
      cancelButtonText: this.transloco.translate('dialog.cancel'),
    }).subscribe((confirmed) => {
      if (!confirmed) return;
      this.pythonService.deleteGlossaryTerm(row.rowId).subscribe({
        next: () => {
          this.data = this.data.filter((r) => r !== row);
        },
        error: (err) => {
          const detail = err?.error?.detail;
          let msg: string;
          if (Array.isArray(detail)) {
            msg = detail.map((d: any) => (d?.loc ? `${d.loc.join('.')}: ${d.msg ?? ''}` : d?.msg ?? JSON.stringify(d))).join('\n');
          } else {
            msg = typeof detail === 'string' ? detail : (err?.error?.message ?? err?.message ?? this.transloco.translate('glossary.deleteError'));
          }
          this.alertService.error(
            this.transloco.translate('glossary.deleteErrorTitle'),
            msg || this.transloco.translate('glossary.deleteError'),
            this.transloco.translate('dialog.ok')
          );
        },
      });
    });
  }

  private showSaveError(err: any): void {
    const detail = err?.error?.detail;
    let msg: string;
    if (Array.isArray(detail)) {
      msg = detail.map((d: any) => (d?.loc ? `${d.loc.join('.')}: ${d.msg ?? ''}` : d?.msg ?? JSON.stringify(d))).join('\n');
    } else {
      msg = typeof detail === 'string' ? detail : (err?.error?.message ?? err?.message ?? this.transloco.translate('glossary.saveError'));
    }
    this.alertService.error(
      this.transloco.translate('glossary.saveErrorTitle'),
      msg || this.transloco.translate('glossary.saveError'),
      this.transloco.translate('dialog.ok')
    );
  }

  saveGlossary(glossary: GlossaryModel) {
    this.pythonService.setGlossaryTerms(glossary).subscribe();
  }

  exportExcel(): void {
    let currLanguage = this.transloco.getActiveLang();
    this.pythonService.exportGlossaryExcel(currLanguage).subscribe({
      next: (blob) => {
        saveAs(blob, 'business_glossary.xlsx');
      },
      error: (err) => {
        const msg = err?.error?.message ?? err?.message ?? this.transloco.translate('glossary.exportError');
        this.alertService.error(
          this.transloco.translate('glossary.exportExcelTitle'),
          msg,
          this.transloco.translate('dialog.ok')
        );
      },
    });
  }

  // Открыть выбор файла для импорта
  triggerImport(): void {
    this.importFileInput?.nativeElement?.click();
  }

  onImportFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;
    input.value = '';
    this.pythonService.importGlossaryExcel(file).subscribe({
      next: () => {
        this.pythonService.getGlossaryTerms().subscribe((response) => {
          this.data = (response.rows ?? []).map((r: any) => ({
            rowId: r.row_id,
            term: r.term ?? '',
            meaning: r.meaning ?? '',
            isEdit: false,
            draftTerm: r.term ?? '',
            draftMeaning: r.meaning ?? '',
          }));
        });
        this.alertService.success(
          this.transloco.translate('glossary.importExcelTitle'),
          this.transloco.translate('glossary.importSuccess'),
          this.transloco.translate('dialog.ok')
        );
      },
      error: (err) => {
        const detail = err?.error?.detail;
        const msg = typeof detail === 'string' ? detail : (err?.error?.message ?? err?.message ?? this.transloco.translate('glossary.importError'));
        this.alertService.error(
          this.transloco.translate('glossary.importExcelTitle'),
          msg,
          this.transloco.translate('dialog.ok')
        );
      },
    });
  }
}
